// src/lib/onboarding.ts
import { doc, setDoc, collection, addDoc, serverTimestamp, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { NewCaseInput, InviteInput } from '../types/onboarding';
import { validateJurisdictionKey } from './jurisdictionValidator';

/**
 * Create a new case and set the user as owner
 */
export async function createCaseAndOwner(input: NewCaseInput, uid: string): Promise<string> {
  if (!input.name || input.name.trim().length < 3) {
    throw new Error('Case name must be at least 3 characters');
  }

  // REQUIRED: Validate jurisdiction key (WO 2.2 - Backend Enforcement)
  validateJurisdictionKey(input.jurisdictionKey, 'jurisdictionKey');

  const casesCol = collection(db, 'cases');
  const newCaseDoc = await addDoc(casesCol, {
    title: input.name.trim(),
    name: input.name.trim(),
    jurisdictionKey: input.jurisdictionKey, // REQUIRED field
    children: input.children || [],
    ownerId: uid,
    ownerUid: uid, // legacy field for backward compatibility
    memberIds: [uid],
    memberUids: [uid], // legacy field for backward compatibility
    partyId: uid,
    counselIds: [],
    evaluatorIds: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    status: 'active',
    storageUsedBytes: 0, // TODO: WO-STORAGE-QUOTA-001 — increment on R2 upload, enforce per plan
  });

  // Create owner member association
  await setDoc(doc(db, 'cases', newCaseDoc.id, 'caseMembers', uid), {
    uid,
    role: 'owner',
    status: 'active',
    joinedAt: serverTimestamp(),
    addedBy: uid,
  });

  return newCaseDoc.id;
}

/**
 * Invite members to a case.
 * Delegates to the `createMemberInvite` Cloud Function which:
 *   - Creates invitation docs in the root `invitations` collection
 *   - Sends an invitation email to each recipient via SendGrid
 *   - Creates an in-app notification for any invitee who already has an account
 *
 * Returns an array of generated invite IDs so the caller can share /invites/{id} links.
 */
export async function inviteMembers(
  caseId: string,
  invites: InviteInput[],
  _uid: string, // retained for API compatibility — auth is verified server-side
): Promise<string[]> {
  if (!caseId) {
    throw new Error('Case ID is required');
  }

  const createMemberInviteFn = httpsCallable<
    { caseId: string; invites: InviteInput[] },
    { inviteIds: string[] }
  >(functions, 'createMemberInvite');

  const result = await createMemberInviteFn({ caseId, invites });
  return result.data.inviteIds;
}

/**
 * Create first journal entry for a case
 */
export async function createFirstEntry(
  caseId: string, 
  narrative: string, 
  tags: string[], 
  uid: string
): Promise<string> {
  if (!narrative || narrative.trim().length === 0) {
    throw new Error('Narrative cannot be empty');
  }

  const entriesCol = collection(db, 'cases', caseId, 'journalEntries');
  const now = new Date().toISOString();

  const newEntry = await addDoc(entriesCol, {
    caseId,
    createdBy: uid,
    createdAt: serverTimestamp(),
    eventTime: now,
    content: narrative.trim(),
    tags: tags || [],
    suggestedTags: [],
    attachments: {
      photos: [],
      audio: [],
      documents: [],
    },
    visibility: 'case',
    status: 'final',
    updatedAt: serverTimestamp(),
    lastEditedBy: uid,
  });

  return newEntry.id;
}

/**
 * Get invite details by inviteId
 */
export async function getInvite(inviteId: string): Promise<{
  id: string;
  caseId: string;
  inviterUid: string;
  inviteeEmail: string;
  role: string;
  status: string;
  createdAt: any;
} | null> {
  try {
    const inviteRef = doc(db, 'invitations', inviteId);
    const inviteSnap = await getDoc(inviteRef);
    
    if (!inviteSnap.exists()) {
      return null;
    }
    
    return {
      id: inviteSnap.id,
      ...inviteSnap.data(),
    } as any;
  } catch (error) {
    console.error('[getInvite] Error:', error);
    throw error;
  }
}

/**
 * Accept a case invite via Cloud Function (Admin SDK bypasses security rules —
 * the invitee cannot write to cases/{caseId} or members/{uid} before being added).
 */
export async function acceptMemberInvite(inviteId: string): Promise<{
  caseId: string;
  redirectPath: string;
  alreadyMember: boolean;
}> {
  const fn = httpsCallable<
    { inviteId: string },
    { caseId: string; redirectPath: string; alreadyMember: boolean }
  >(functions, 'acceptMemberInvite');
  const result = await fn({ inviteId });
  return result.data;
}

/**
 * Accept a case invite
 * @deprecated Use acceptMemberInvite (CF) instead — direct Firestore writes
 * fail because the invitee lacks write access to the case doc before membership
 * is granted.
 */
export async function acceptInvite(inviteId: string, userUid: string, userEmail: string): Promise<{
  success: boolean;
  caseId?: string;
  error?: string;
}> {
  try {
    // Get invite details
    const inviteRef = doc(db, 'invitations', inviteId);
    const inviteSnap = await getDoc(inviteRef);
    
    if (!inviteSnap.exists()) {
      return { success: false, error: 'Invite not found' };
    }
    
    const inviteData = inviteSnap.data();
    
    // Verify email matches
    if (inviteData.inviteeEmail !== userEmail) {
      return { success: false, error: 'This invite was sent to a different email address' };
    }
    
    // Check if already accepted
    if (inviteData.status === 'accepted') {
      return { success: true, caseId: inviteData.caseId }; // Already accepted, return success
    }
    
    // Check if declined
    if (inviteData.status === 'declined') {
      return { success: false, error: 'This invite was already declined' };
    }
    
    const caseId = inviteData.caseId;
    
    // Update invite status
    await updateDoc(inviteRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedBy: userUid,
      updatedAt: serverTimestamp(),
    });
    
    // Add user to case memberIds array
    const caseRef = doc(db, 'cases', caseId);
    await updateDoc(caseRef, {
      memberIds: arrayUnion(userUid),
    });
    
    // Create membership document
    await setDoc(doc(db, 'cases', caseId, 'members', userUid), {
      userId: userUid,
      caseId,
      role: inviteData.role || 'editor',
      invitedBy: inviteData.inviterUid,
      inviteAcceptedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return { success: true, caseId };
  } catch (error) {
    console.error('[acceptInvite] Error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to accept invite' };
  }
}

/**
 * Decline a case invite
 */
export async function declineInvite(inviteId: string, userUid: string, userEmail: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // Get invite details
    const inviteRef = doc(db, 'invitations', inviteId);
    const inviteSnap = await getDoc(inviteRef);
    
    if (!inviteSnap.exists()) {
      return { success: false, error: 'Invite not found' };
    }
    
    const inviteData = inviteSnap.data();
    
    // Verify email matches
    if (inviteData.inviteeEmail !== userEmail) {
      return { success: false, error: 'This invite was sent to a different email address' };
    }
    
    // Check if already accepted
    if (inviteData.status === 'accepted') {
      return { success: false, error: 'This invite was already accepted' };
    }
    
    // Update invite status
    await updateDoc(inviteRef, {
      status: 'declined',
      declinedAt: serverTimestamp(),
      declinedBy: userUid,
      updatedAt: serverTimestamp(),
    });
    
    return { success: true };
  } catch (error) {
    console.error('[declineInvite] Error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to decline invite' };
  }
}
